<template>
  <div class="video-progress-bar">
    <div class="bar-header">
      <div class="type-wrapper-tipbar">
        <div class="tag-tag">
          <!-- Tracker 数据加载 -->
          <LoadingOutlined v-if="playLoading" class="tag-icon tag-loading" />
          <span v-show="playLoading" class="loading-text">加载中...</span>
          <div
            class="custom-tag"
            :class="{ 'tag-disabled': !timestampFilters.tracker || playLoading }"
            @click="toggleTimestampFilter('tracker')"
          >
            <span
              v-show="!playLoading"
              class="tag-icon"
              :style="{ backgroundColor: timestampFilters.tracker ? '#52c41a' : '#d9d9d9' }"
            ></span>
            <span v-show="!playLoading" class="tag-text">Tracker 数据</span>
          </div>

          <!-- Tags 数据加载 -->
          <LoadingOutlined v-if="eventAxisLoading" class="tag-icon tag-loading" />
          <span v-show="eventAxisLoading" class="loading-text">加载中...</span>
          <div
            v-for="tag in tags"
            :key="tag.type"
            class="custom-tag"
            :class="{
              'tag-disabled':
                !timestampFilters[tag.type as keyof typeof timestampFilters] || eventAxisLoading,
            }"
            @click="toggleTimestampFilter(tag.type)"
          >
            <span
              v-show="!eventAxisLoading"
              class="tag-icon"
              :style="{
                backgroundColor: timestampFilters[tag.type as keyof typeof timestampFilters]
                  ? tag.color
                  : '#d9d9d9',
              }"
            ></span>
            <span v-show="!eventAxisLoading" class="tag-text">{{ tag.text }}</span>
            <span v-show="!eventAxisLoading" class="tag-count"
              >{{ timestampStats[tag.type as keyof typeof timestampStats] }}条</span
            >
          </div>
        </div>
      </div>
      <div class="time-wrapper">
        <a-date-picker
          v-model:value="dateTime"
          format="YYYY-MM-DD"
          style="width: 140px"
          size="default"
          :bordered="true"
          :allow-clear="false"
          :disabled="loading"
          placement="topRight"
          @change="onDateTimeChange"
        />

        <a-space :size="6">
          <a-input-number
            v-model:value="localSpeedRate"
            :min="0.1"
            style="width: 100px"
            size="default"
            :controls="false"
            addon-before="倍率"
            @blur="handleSpeedChange"
            @focus="handleSpeedChange"
          />

          <a-time-picker
            v-model:value="timePickerValue"
            style="width: 100px"
            size="default"
            :allow-clear="false"
            :show-now="false"
            @blur="handleTimeChange"
            @focus="handleTimeChange"
          />

          <div class="fast-btn-operation">
            <a-button
              type="text"
              title="快退n秒"
              :disabled="!timeFastVale"
              @click="handleFastBackward"
            >
              <template #icon>
                <FastBackwardOutlined />
              </template>
            </a-button>

            <a-input-number
              v-model:value="timeFastVale"
              :min="0"
              :bordered="true"
              :controls="false"
              size="small"
              placeholder="n秒"
              title="快进/退:n秒"
              style="width: 50px; padding: 0; text-align: center"
            />

            <a-button
              type="text"
              title="快进n秒"
              :disabled="!timeFastVale"
              @click="handleFastForward"
            >
              <template #icon>
                <FastForwardOutlined />
              </template>
            </a-button>
          </div>

          <div class="button-wrapper">
            <a-button
              v-if="playing"
              type="text"
              :disabled="loading"
              @click="handlePause"
              title="暂停"
            >
              <template #icon>
                <PauseCircleOutlined class="action-icon" />
              </template>
              <span class="action-text">暂停</span>
            </a-button>
            <a-button
              v-else
              type="text"
              :disabled="loading || playLoading"
              @click="handlePlay"
              title="播放"
            >
              <template #icon>
                <PlayCircleOutlined class="action-icon" />
              </template>
              <span class="action-text">播放</span>
            </a-button>
          </div>
        </a-space>
      </div>
    </div>

    <div class="main-container" ref="mainContainer">
      <canvas
        ref="canvasRef"
        @mousemove="handleMouseMove"
        @mouseleave="handleMouseLeave"
        @click="handleClick"
      ></canvas>

      <!-- 悬停竖线 -->
      <div v-if="showHoverInfo" class="hover-line" :style="{ left: `${hoverPosition.x}px` }"></div>

      <!-- 悬停信息提示 -->
      <div v-if="showHoverInfo" class="hover-info" :style="{ left: `${hoverPosition.x}px` }">
        时间: {{ hoverInfo.time }}
      </div>

      <!-- 播放线信息展示 -->
      <div
        v-if="playing && currentFrameTs"
        class="play-line-info"
        :class="{ 'play-line-glow': playing }"
        :style="{ left: `${playLinePosition.x}px` }"
      >
        时间: {{ playLineInfo.time }} 高度：{{ currentZInfo }}
      </div>
    </div>

    <!-- 工具栏区域 -->
    <div class="control-buttons">
      <div class="timestampList">
        <div
          v-for="event in filteredTimestampList"
          :key="event.id"
          class="timestamp-tag"
          :style="{ backgroundColor: event.color }"
          @click="handleTimestampClick(event)"
        >
          <span class="tag-icon" :style="{ backgroundColor: event.iconColor }"></span>
          <span class="tag-text">{{ event.timeRange }}</span>
        </div>
      </div>
    </div>

    <div class="type-wrapper">
      <div class="tag-tag">
        <div
          v-for="tag in tags.filter((tag) => tag.type !== 'inbed')"
          :key="tag.type"
          class="custom-tag"
          :class="{
            'tag-selected': selectedTags.includes(tag.type),
            'tag-disabled': eventAxisLoading,
          }"
          :style="{ backgroundColor: eventAxisLoading ? '#d9d9d9' : tag.color }"
          @click="selectTag(tag.type)"
        >
          <span class="tag-text">{{ tag.text }}</span>
          <div
            v-if="selectedTags.includes(tag.type) && !eventAxisLoading"
            class="tag-selected-overlay"
          >
            <div class="tag-selected-mask"></div>
            <CheckOutlined class="tag-check" />
          </div>
        </div>
      </div>
      <a-button
        type="primary"
        size="small"
        :disabled="selectedTags.length === 0 || listLoading || eventAxisLoading"
        @click="fetchDoenloadList"
      >
        <span>加载列表</span>
      </a-button>
      <a-button
        type="primary"
        size="small"
        :disabled="!hasSelected || eventAxisLoading"
        @click="handleBatchDownload"
      >
        批量下载 {{ selectedRowKeys.length > 0 ? selectedRowKeys.length + '条' : '' }}
      </a-button>
    </div>
    <a-table
      :columns="columns"
      :data-source="downloadFileList"
      :loading="listLoading"
      :pagination="false"
      size="small"
      :scroll="{ y: 300 }"
      :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
      :row-key="(record: string) => record"
    >
      <template #bodyCell="{ column, record, index }">
        <template v-if="column.key === 'index'">
          <div>{{ index + 1 }}</div>
        </template>
        <template v-if="column.key === 'eventName'">
          <div>{{ record.eventName }}</div>
        </template>
        <template v-if="column.key === 'operation'">
          <a-space>
            <a-button
              type="link"
              size="small"
              @click="handleDownloadOne(record.downloadUrl, record.eventName)"
              >下载</a-button
            >
          </a-space>
        </template>
      </template>
    </a-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, reactive } from 'vue'
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckOutlined,
  LoadingOutlined,
  FastBackwardOutlined,
  FastForwardOutlined,
} from '@ant-design/icons-vue'
import dayjs, { type Dayjs } from 'dayjs'
import { message } from 'ant-design-vue'
import { convert_point_r2c } from '@/utils/coordTransform'
import { queryDeviceEventTimeNodes, queryEventTimeNodeFileUrls } from '@/api/event'
import type { DeviceEventTimeNode } from '@/api/event/types'

interface Props {
  devId: string
  clientId: string
  angle: number
}

const props = withDefaults(defineProps<Props>(), {
  devId: '',
  clientId: '',
  angle: 0,
})

const emit = defineEmits<{
  (e: 'targets2dChange', targets2d: Record<number, TargetInfo>): void
  (e: 'pointCloud3dChange', pointClouds: CloudPoint[]): void
  (e: 'radarTargetsChange', radarTargets: Array<[number, number, number, number]>): void
}>()

defineOptions({ name: 'ReplayProgressBar' })

/* 进度条相关 */
const clientId = computed(() => props.clientId)
const devId = computed(() => props.devId)
const dateTime = ref(dayjs())
const loading = defineModel<boolean>('loading', { default: false })
const playing = defineModel<boolean>('playing', { default: false })

// 事件轴数据加载状态
const eventAxisLoading = ref(false)

/* 片段标签 start */
const tags = ref([
  { color: '#7b70bc', text: '人员存在', type: 'inout' },
  { color: '#ad6d64', text: '跌倒事件', type: 'fall' },
  { color: '#f8a235', text: '在床', type: 'inbed' },
  // { color: '#c9b67c', text: '如厕', type: 'toilet' },
])

// 选中的标签数组
const selectedTags = ref<string[]>([])

// 选择标签的方法
const selectTag = (type: string) => {
  // 多选模式：点击添加/取消选择
  const index = selectedTags.value.indexOf(type)
  if (index > -1) {
    // 取消选择
    selectedTags.value.splice(index, 1)
  } else {
    // 添加选择
    selectedTags.value.push(type)
  }
}

// 处理下载事件
const fetchDoenloadList = () => {
  fetchEventDownloadUrl()
}
/* 片段标签 end */

const onDateTimeChange = (v: Dayjs) => {
  console.log('选择的时间:', v.format('YYYY-MM-DD'))
  // 重置播放状态
  playing.value = false
  // 断开WebSocket连接
  resetWsRef()
  // 重置当前帧时间戳
  currentFrameTs.value = new Date().setHours(0, 0, 0, 0)
  // 重新获取事件轴数据
  fetchEventAxisData()
  fetchAllDayTarckerData()
}

interface TrackerSegment {
  start_ts: number
  end_ts: number
}

// 绿色进度条
const trackerSegments = ref<Array<TrackerSegment>>([])
const effectTimeRange = ref<timeItem>({ start_ts: 0, end_ts: 0 })
// const filePath = ref('')

const wsRef = ref<WebSocket>()
const resetWsRef = () => {
  if (wsRef.value) {
    wsRef.value.close()
    wsRef.value = undefined
  }
}

// 用于取消之前的请求
const fetchController = ref<AbortController | null>(null)

const eventAxisData = ref<DeviceEventTimeNode>({
  fallEvent: [],
  personInOutEvent: [],
  bedInOutEvent: [],
})

// 时间筛选器选中状态
const timestampFilters = ref({
  inout: true,
  fall: true,
  inbed: true,
  tracker: true, // trackerSegments 的筛选状态
})

// 整合事件轴数据为时间戳 tag 列表
const timestampList = computed(() => {
  const result: Array<{
    id: string
    type: string
    timeRange: string
    startTime: number
    endTime: number
    color: string
    iconColor: string
  }> = []

  // 处理人员存在事件（时间段）
  eventAxisData.value.personInOutEvent.forEach((event, index) => {
    const enterTime = Number(event.enterTime) * 1000
    const leaveTime = event.leaveTime ? Number(event.leaveTime) * 1000 : null
    const personTag = tags.value.find((tag) => tag.type === 'inout')

    // 如果有结束时间，显示时间段；否则显示开始时间和"--"
    const timeRange = leaveTime
      ? `${formatTime(Math.round(enterTime))} ~ ${formatTime(Math.round(leaveTime))}`
      : `${formatTime(Math.round(enterTime))} ~ --`

    result.push({
      id: `inout-${index}-${enterTime}`,
      type: 'inout',
      timeRange: timeRange,
      startTime: enterTime,
      endTime: leaveTime || new Date().getTime(),
      color: personTag ? personTag.color + '40' : '#7b70bc40', // 添加透明度
      iconColor: personTag ? personTag.color : '#7b70bc',
    })
  })

  // 处理跌倒事件（时间点）
  eventAxisData.value.fallEvent.forEach((event, index) => {
    const eventTime = new Date(event.createTime).getTime()
    const fallTag = tags.value.find((tag) => tag.type === 'fall')

    result.push({
      id: `fall-${index}-${eventTime}`,
      type: 'fall',
      timeRange: formatTime(Math.round(eventTime)),
      startTime: eventTime,
      endTime: eventTime,
      color: fallTag ? fallTag.color + '40' : '#ad6d6440', // 添加透明度
      iconColor: fallTag ? fallTag.color : '#ad6d64',
    })
  })

  // 处理在床事件（时间段）
  if (Array.isArray(eventAxisData.value.bedInOutEvent)) {
    eventAxisData.value.bedInOutEvent.forEach((event, index) => {
      const enterTime = Number(event.enterTime) * 1000
      const leaveTime = event.leaveTime ? Number(event.leaveTime) * 1000 : null
      const inBedTag = tags.value.find((tag) => tag.type === 'inbed')

      // 如果有结束时间，显示时间段；否则显示开始时间和"--"
      const timeRange = leaveTime
        ? `${formatTime(Math.round(enterTime))} ~ ${formatTime(Math.round(leaveTime))}`
        : `${formatTime(Math.round(enterTime))} ~ --`

      result.push({
        id: `inbed-${index}-${enterTime}`,
        type: 'inbed',
        timeRange: timeRange,
        startTime: enterTime,
        endTime: leaveTime || new Date().getTime(),
        color: inBedTag ? inBedTag.color + '40' : '#f8a23540', // 添加透明度
        iconColor: inBedTag ? inBedTag.color : '#f8a235',
      })
    })
  }

  // 按时间排序
  return result.sort((a, b) => a.startTime - b.startTime)
})

// 筛选后的时间戳列表
const filteredTimestampList = computed(() => {
  return timestampList.value.filter((item) => {
    return timestampFilters.value[item.type as keyof typeof timestampFilters.value]
  })
})

// 统计每种类型的数量
const timestampStats = computed(() => {
  const stats = {
    inout: 0,
    fall: 0,
    inbed: 0,
  }
  timestampList.value.forEach((item) => {
    if (item.type === 'inout') {
      stats.inout++
    } else if (item.type === 'fall') {
      stats.fall++
    } else if (item.type === 'inbed') {
      stats.inbed++
    }
  })
  return stats
})

// 处理时间戳 tag 点击
const handleTimestampClick = (event: { startTime: number; endTime: number }) => {
  // 跳转到事件开始时间
  currentFrameTs.value = event.startTime
  // sendCmd({ cmd: 'seek', ts: Math.round(event.startTime) })
  // 快退 10 秒时间
  timeFastVale.value = timeFastVale.value ?? 10
  handleFastBackward()
  // 重新计算播放线信息
  calculatePlayLineInfo()
  // 重新绘制
  draw()
}

// 切换时间筛选器
const toggleTimestampFilter = (type: string) => {
  if (type === 'inout') {
    timestampFilters.value.inout = !timestampFilters.value.inout
  } else if (type === 'fall') {
    timestampFilters.value.fall = !timestampFilters.value.fall
  } else if (type === 'tracker') {
    timestampFilters.value.tracker = !timestampFilters.value.tracker
  } else if (type === 'inbed') {
    timestampFilters.value.inbed = !timestampFilters.value.inbed
  }
  // 筛选变化后重新绘制 Canvas
  draw()
}

/**
 * 获取事件轴的数据
 * @description 获取的数据包含：人员存在、跌倒事件、如厕事件等
 */
const fetchEventAxisData = async () => {
  if (!clientId.value || !dateTime.value) return

  // 设置加载状态
  eventAxisLoading.value = true

  try {
    const res = await queryDeviceEventTimeNodes({
      devId: devId.value,
      date: dateTime.value.format('YYYY-MM-DD'),
    })
    console.log('获取事件轴数据 成功✅', res.data)
    eventAxisData.value = res.data
    initCanvas()
    // 清空文件路径和下载地址
    // filePath.value = ''
  } catch (error) {
    console.log('获取事件轴数据 失败❌', error)
  } finally {
    // 恢复加载状态
    eventAxisLoading.value = false
    selectedTags.value = []
    personInOutEventDownloadList.value = []
  }
}
fetchEventAxisData()

interface timeItem {
  start_ts: number
  end_ts: number
}

// 分段间隙
const framesGap = ref(3000)
const playLoading = ref(false)

/**
 * 获取全天的事件轴数据
 * @description 获取设备的全量 Tracker 数据
 */
const fetchAllDayTarckerData = async () => {
  if (!clientId.value || !dateTime.value) return
  resetWsRef()

  // 取消之前的请求（如果存在）
  if (fetchController.value) {
    fetchController.value.abort()
    fetchController.value = null
  }

  // 设置加载状态，禁用播放按钮和 Tracker tag
  playLoading.value = true

  // 创建新的 AbortController
  const controller = new AbortController()
  fetchController.value = controller

  // 截止时间参数判断，今天的就是当前时间，否则就是 23:59:59
  const today = dayjs(new Date()).format('YYYY-MM-DD')
  const selectDate = dateTime.value.format('YYYY-MM-DD')
  const endTime =
    today === selectDate
      ? dayjs().format('YYYY-MM-DD') + dayjs(new Date()).format(' HH:mm:ss')
      : selectDate + ' 23:59:59'

  // 设置超时定时器
  const timeout = 3 * 60 * 1000 // 超时时间
  const timeoutId = setTimeout(() => {
    controller.abort()
  }, timeout)

  try {
    // const wsUrl = `${import.meta.env.VITE_VIDEO_PLAY_URL}/oss/query_time_range_record`
    const wsUrl = `${import.meta.env.VITE_VIDEO_PLAY_URL}/oss/query_time_range_target_segment`

    const res = await fetch(wsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        dev_id: clientId.value,
        start_time: dateTime.value.format('YYYY-MM-DD') + ' 00:00:00',
        end_time: endTime,
      }),
      signal: controller.signal,
    })

    // 清除超时定时器
    clearTimeout(timeoutId)

    if (!res.ok) {
      throw new Error(await res.text())
    }

    const data = await res.json()
    console.log('获取回放数据成功✅', res, data)
    // if (!data.records) {
    //   console.log('暂无回放文件🥺', data.records)
    //   return
    // }
    const { time_axis, gap } = data.records
    framesGap.value = gap || 3000

    // 从 frames 数据中过滤并分段
    const segment = (time_axis.segment as timeItem[]) || []
    trackerSegments.value = segment
    effectTimeRange.value = (time_axis.time_range as timeItem) || []

    // console.log('处理后的分段数据:', trackerSegments.value)
    // filePath.value = file_path
  } catch (error) {
    console.log('获取回放数据失败❌', error)
    // filePath.value = ''

    // 判断是否是取消的请求或超时错误
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        // 请求被取消（可能是被新请求取消，也可能是超时）
        // 只有当前 controller 仍然是活跃的，才恢复状态
        if (fetchController.value === controller) {
          console.log('当前请求已被取消或超时，恢复状态')
          playLoading.value = false
        } else {
          console.log('旧请求被取消，忽略（新请求还在进行中）')
        }
        // 清除定时器
        clearTimeout(timeoutId)
        // 清除 controller 引用
        if (fetchController.value === controller) {
          fetchController.value = null
        }
        return
      }
      message.error('请求失败，请重试')
    }
  } finally {
    // 只有当前 controller 仍然是活跃的，才恢复状态
    if (fetchController.value === controller) {
      // 请求完成，恢复状态
      playLoading.value = false
      // 清除定时器（如果还在运行）
      clearTimeout(timeoutId)
      // 清除 controller 引用
      fetchController.value = null
    }
  }
}
fetchAllDayTarckerData()

// Canvas相关
const canvasRef = ref<HTMLCanvasElement | null>(null)
const mainContainer = ref<HTMLElement | null>(null)
let ctx: CanvasRenderingContext2D | null = null

// 视图相关
// 初始化视图为当前日期的24小时范围
const today = dateTime.value.startOf('day')
const startOfDay = today.valueOf()
const endOfDay = today.endOf('day').valueOf()

const viewStart = ref(startOfDay)
const viewEnd = ref(endOfDay)
const viewDuration = computed(() => viewEnd.value - viewStart.value)

// 本地倍速变量，用于双向绑定
const localSpeedRate = ref(1.0)

// 悬停相关
const showHoverInfo = ref(false)
const hoverPosition = ref({ x: 0, y: 0 })
const hoverInfo = ref({ time: '', height: 0 })

// 播放线信息
const playLineInfo = ref({ time: '', height: 0 })
// 播放线位置
const playLinePosition = ref({ x: 0 })

const currentZInfo = ref(0)
// 计算播放线位置的时间和高度
const calculatePlayLineInfo = () => {
  // 使用当前播放的时间戳，如果没有或为0则从00:00:00开始
  const currentTime = currentFrameTs.value > 0 ? currentFrameTs.value : viewStart.value

  playLineInfo.value = {
    time: formatTime(Math.round(currentTime)),
    height: currentZInfo.value,
  }
}

// 格式化时间显示（时分秒）
const formatTime = (ts: number) => {
  const date = new Date(ts)

  // 检查是否是当天的24:00:00
  if (ts >= viewEnd.value) {
    return '24:00:00'
  }

  return date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

// 初始化Canvas
const initCanvas = () => {
  if (!canvasRef.value || !mainContainer.value) return

  ctx = canvasRef.value.getContext('2d')
  if (!ctx) return

  // 设置Canvas尺寸
  resizeCanvas()

  // 固定设置为24小时时间范围（00:00:00 - 23:59:59）
  const today = dateTime.value.startOf('day')
  const startOfDay = today.valueOf()
  const endOfDay = today.endOf('day').valueOf()

  // 初始视图设置为整个24小时
  viewStart.value = startOfDay
  viewEnd.value = endOfDay

  // 计算初始播放线信息
  calculatePlayLineInfo()

  // 绘制初始内容
  draw()
}

onMounted(() => {
  // 添加窗口大小变化监听
  window.addEventListener('resize', resizeCanvas)
})

// 销毁时清理
onUnmounted(() => {
  window.removeEventListener('resize', resizeCanvas)
})

// 监听数据变化，重新绘制
watch(
  () => [eventAxisData.value, trackerSegments.value, timestampFilters.value],
  () => {
    draw()
  },
  { deep: true }
)

// 调整Canvas尺寸
const resizeCanvas = () => {
  if (!canvasRef.value || !mainContainer.value) return

  const containerRect = mainContainer.value.getBoundingClientRect()
  // 保持Canvas尺寸与容器一致
  canvasRef.value.width = containerRect.width
  canvasRef.value.height = containerRect.height

  draw()
}

// 绘制整个Canvas
const draw = () => {
  if (!ctx || !canvasRef.value) return

  const canvas = canvasRef.value
  const { width, height } = canvas

  // 清空画布
  ctx.clearRect(0, 0, width, height)

  // 绘制背景
  drawBackground()

  // 绘制有效片段和帧条
  drawSegments()

  // 绘制时间刻度
  drawTimeScale()

  // 绘制播放线
  drawPlayLine()
}

// 绘制背景
const drawBackground = () => {
  if (!ctx || !canvasRef.value) return

  const { width, height } = canvasRef.value
  const eventAxisHeight = 45 // 事件轴区域高度
  const trackerHeight = 15 // trackerSegments 区域高度

  // 整个进度条背景为米白色
  ctx.fillStyle = '#f5f5dc'
  ctx.fillRect(0, 0, width, height)

  // 事件轴区域背景为淡灰色
  ctx.fillStyle = '#f0f0f0'
  ctx.fillRect(0, 0, width, eventAxisHeight)

  // trackerSegments 区域背景根据筛选状态变化
  if (timestampFilters.value.tracker) {
    // 启用时显示稍深的灰色，以示区分
    ctx.fillStyle = '#e8e8e8'
  } else {
    // 禁用时显示更淡的灰色，表示不可用
    ctx.fillStyle = '#f5f5f5'
  }
  ctx.fillRect(0, eventAxisHeight, width, trackerHeight)
}

// 绘制事件轴
const drawSegments = () => {
  if (!ctx || !canvasRef.value) return

  const { width } = canvasRef.value
  const eventAxisHeight = 45 // 事件轴区域高度（0-45px）
  const trackerHeight = 15 // trackerSegments 区域高度（45-60px）
  const trackerY = eventAxisHeight // tracker 起始 Y 坐标

  // 绘制人员存在事件（时间段）- 受筛选控制
  if (timestampFilters.value.inout) {
    eventAxisData.value.personInOutEvent.forEach((event) => {
      // 将字符串类型的时间戳转换为数字类型，并将秒级时间戳转换为毫秒级时间戳
      const enterTime = Number(event.enterTime) * 1000
      // 如果 leaveTime 为 null 或不存在，则设置为当前时间
      const leaveTime = event.leaveTime ? Number(event.leaveTime) * 1000 : new Date().getTime()

      // 检查事件是否在视图范围内
      if (leaveTime < viewStart.value || enterTime > viewEnd.value) {
        return
      }

      // 计算事件在 Canvas 中的位置
      const left = ((enterTime - viewStart.value) / viewDuration.value) * width
      const right = ((leaveTime - viewStart.value) / viewDuration.value) * width
      const eventWidth = right - left

      if (eventWidth <= 0 || !ctx) return

      // 使用 tags 中对应的颜色（人员存在）
      const personTag = tags.value.find((tag) => tag.type === 'inout')
      const color = personTag ? personTag.color : '#7b70bc'

      ctx.fillStyle = color
      ctx.fillRect(left, 0, eventWidth, eventAxisHeight)
    })
  }

  // 绘制跌倒事件（时间点）- 受筛选控制
  if (timestampFilters.value.fall) {
    eventAxisData.value.fallEvent.forEach((event) => {
      const eventTime = new Date(event.createTime).getTime()

      // 检查事件是否在视图范围内
      if (eventTime < viewStart.value || eventTime > viewEnd.value) {
        return
      }

      // 计算事件在 Canvas 中的位置
      const eventX = ((eventTime - viewStart.value) / viewDuration.value) * width

      // 使用 tags 中对应的颜色（跌倒事件）
      const fallTag = tags.value.find((tag) => tag.type === 'fall')
      const color = fallTag ? fallTag.color : '#ad6d64'

      // 绘制跌倒事件标记（垂直线条）
      if (ctx) {
        ctx.fillStyle = color
        ctx.fillRect(eventX - 2, 0, 4, eventAxisHeight)
      }
    })
  }

  // 绘制在床事件（时间段）- 受筛选控制
  if (timestampFilters.value.inbed && Array.isArray(eventAxisData.value.bedInOutEvent)) {
    eventAxisData.value.bedInOutEvent.forEach((event) => {
      // 将字符串类型的时间戳转换为数字类型，并将秒级时间戳转换为毫秒级时间戳
      const enterTime = Number(event.enterTime) * 1000
      // 如果 leaveTime 为 null 或不存在，则设置为当前时间
      const leaveTime = event.leaveTime ? Number(event.leaveTime) * 1000 : new Date().getTime()

      // 检查事件是否在视图范围内
      if (leaveTime < viewStart.value || enterTime > viewEnd.value) {
        return
      }

      // 计算事件在 Canvas 中的位置
      const left = ((enterTime - viewStart.value) / viewDuration.value) * width
      const right = ((leaveTime - viewStart.value) / viewDuration.value) * width
      const eventWidth = right - left

      if (eventWidth <= 0 || !ctx) return

      // 使用 tags 中对应的颜色（在床）
      const inBedTag = tags.value.find((tag) => tag.type === 'inbed')
      const color = inBedTag ? inBedTag.color : '#f8a235'

      ctx.fillStyle = color
      ctx.fillRect(left, 0, eventWidth, eventAxisHeight)
    })
  }

  // 绘制 trackerSegments（绿色，位于事件轴下方，时间刻度上方）- 受筛选控制
  if (timestampFilters.value.tracker) {
    trackerSegments.value.forEach((segment) => {
      const { start_ts, end_ts } = segment

      // 检查片段是否在视图范围内
      if (end_ts < viewStart.value || start_ts > viewEnd.value) {
        return
      }

      // 计算在 Canvas 中的位置
      const left = ((start_ts - viewStart.value) / viewDuration.value) * width
      const right = ((end_ts - viewStart.value) / viewDuration.value) * width
      const segmentWidth = right - left

      if (segmentWidth <= 0 || !ctx) return

      // 使用绿色绘制 tracker 片段
      ctx.fillStyle = '#52c41a'
      ctx.fillRect(left, trackerY, segmentWidth, trackerHeight)
    })
  }
}

// 绘制时间刻度
const drawTimeScale = () => {
  if (!ctx || !canvasRef.value) return

  const { width, height } = canvasRef.value
  const scaleY = height - 30 // 刻度起始位置

  // 时间刻度背景色
  ctx.fillStyle = '#f9f9f9'
  ctx.fillRect(0, scaleY, width, 30)

  ctx.fillStyle = '#666'
  ctx.font = '10px Arial'

  // 定义需要显示的整点时间（小时）
  const hours = [0, 6, 12, 18, 24]

  // 绘制刻度
  hours.forEach((hour, index) => {
    if (!ctx) return
    // 计算该小时对应的时间戳
    const ts = viewStart.value + hour * 60 * 60 * 1000
    // 计算在Canvas中的位置
    const x = (hour / 24) * width

    // 绘制刻度线
    ctx.beginPath()
    ctx.moveTo(x, scaleY)
    ctx.lineTo(x, scaleY + 8)
    ctx.strokeStyle = '#999'
    ctx.lineWidth = 1
    ctx.stroke()

    // 绘制时间文本，根据位置调整对齐方式
    const timeText = formatTime(Math.round(ts))

    // 最左侧文本左对齐，最右侧文本右对齐，中间文本居中对齐
    if (index === 0) {
      ctx.textAlign = 'left'
      // 向左偏移2px，避免紧贴边缘
      ctx.fillText(timeText, x + 2, scaleY + 20)
    } else if (index === hours.length - 1) {
      ctx.textAlign = 'right'
      // 向右偏移2px，避免紧贴边缘
      ctx.fillText(timeText, x - 2, scaleY + 20)
    } else {
      ctx.textAlign = 'center'
      ctx.fillText(timeText, x, scaleY + 20)
    }
  })
}

// 绘制播放线
const drawPlayLine = () => {
  if (!ctx || !canvasRef.value || !mainContainer.value) return

  const { width, height } = canvasRef.value
  const progressHeight = height - 30 // 播放线贯穿事件轴和 trackerSegments 区域，不到刻度区域

  // 只有在播放状态或当前帧时间戳大于 0 时才绘制播放线
  if (playing.value || currentFrameTs.value > 0) {
    // 根据当前播放时间计算播放线的位置
    // 如果没有当前播放时间或 currentFrameTs 为 0，则从 00:00:00 开始
    const currentTime = currentFrameTs.value > 0 ? currentFrameTs.value : viewStart.value
    // 计算播放线在 Canvas 中的位置
    const playLineX = ((currentTime - viewStart.value) / viewDuration.value) * width

    // 绘制播放线，贯穿事件轴和 trackerSegments 区域
    ctx.beginPath()
    ctx.moveTo(playLineX, 0)
    ctx.lineTo(playLineX, progressHeight)
    ctx.strokeStyle = '#1677ff'
    ctx.lineWidth = 2
    ctx.stroke()

    // 更新播放线位置，用于显示播放线信息
    playLinePosition.value = { x: playLineX }
  }
}

// 将屏幕坐标转换为时间
const screenToTime = (x: number) => {
  if (!canvasRef.value) return 0

  const { width } = canvasRef.value
  const percentage = x / width
  return viewStart.value + percentage * viewDuration.value
}

// 处理鼠标移动（只保留悬停逻辑，移除拖拽逻辑）
const handleMouseMove = (e: MouseEvent) => {
  if (!mainContainer.value || !canvasRef.value) return

  const containerRect = mainContainer.value.getBoundingClientRect()
  const mouseX = e.clientX - containerRect.left
  const mouseY = e.clientY - containerRect.top

  // 处理悬停
  const hoverTime = screenToTime(mouseX)

  // 总是显示悬停信息，只要鼠标在事件轴上
  showHoverInfo.value = true
  hoverPosition.value = { x: mouseX, y: mouseY }
  hoverInfo.value = {
    time: formatTime(Math.round(hoverTime)),
    height: currentZInfo.value,
  }
}

// 处理鼠标离开
const handleMouseLeave = () => {
  showHoverInfo.value = false
}

// 处理单击事件
const handleClick = (e: MouseEvent) => {
  if (!mainContainer.value || !canvasRef.value) return

  const containerRect = mainContainer.value.getBoundingClientRect()
  const mouseX = e.clientX - containerRect.left
  const clickTime = screenToTime(mouseX)

  // 检查点击位置是否有有效的事件数据（人员存在或跌倒事件）
  // const hasEventAtTime = filteredTimestampList.value.some((event) => {
  //   // 对于时间段事件（人员存在），检查是否在该时间段内
  //   if (event.startTime !== event.endTime) {
  //     return clickTime >= event.startTime && clickTime <= event.endTime
  //   }
  //   // 对于时间点事件（跌倒），检查是否在该时间点附近（允许 1 秒误差）
  //   return Math.abs(clickTime - event.startTime) < 1000
  // })

  // 如果没有事件数据，不响应点击
  // if (!hasEventAtTime) {
  //   return
  // }

  console.log('handleClickBar timestamps: ', clickTime)

  // 直接跳转到点击位置
  currentFrameTs.value = clickTime
  sendCmd({ cmd: 'seek', ts: Math.round(clickTime) })
  // 重新计算播放线信息
  calculatePlayLineInfo()
  // 重新绘制
  draw()
}

// 点云数据类型 - [x, y, z, intensity, id]
type CloudPoint = [number, number, number, number, number]

interface WebMessageType {
  payload: {
    tracker_targets: Array<[number, number, number, number]>
    cloud_points: CloudPoint[]
  }
  ts: number
  health: object
  z_info: number | null
  msg: string
  error: string
}

interface TargetInfo {
  x: number
  y: number
  z: number
  id: number
  displayX: number
  displayY: number
  lastX: number
  lastY: number
}

// 播放相关状态
const targets = reactive<Record<number, TargetInfo>>({})

const currentFrameTs = ref<number>(new Date().setHours(0, 0, 0, 0))

// 用于时间选择器的计算属性（支持双向绑定）
const timePickerValue = computed({
  get: () => dayjs(currentFrameTs.value),
  set: (val: Dayjs | null) => {
    if (val) {
      const newTs = val.valueOf()
      currentFrameTs.value = newTs
      // 发送跳转命令
      sendCmd({ cmd: 'seek', ts: Math.round(newTs) })
      // 重新计算播放线信息
      calculatePlayLineInfo()
      // 重新绘制
      draw()
    }
  },
})

const timeFastVale = ref<number>(10)

// 快退 n 秒时间
const handleFastBackward = () => {
  const second = timeFastVale.value
  const fastBackwardTime = second * 1000
  console.log('快退', second + '秒', fastBackwardTime + '毫秒')
  currentFrameTs.value = currentFrameTs.value - fastBackwardTime
  sendCmd({ cmd: 'seek', ts: Math.round(currentFrameTs.value) })
}

// 快进 n 秒时间
const handleFastForward = () => {
  const second = timeFastVale.value
  const fastForwardTime = second * 1000
  console.log('快进', second + '秒', fastForwardTime + '毫秒')
  currentFrameTs.value = currentFrameTs.value + fastForwardTime
  sendCmd({ cmd: 'seek', ts: Math.round(currentFrameTs.value) })
}

// 监听视图变化，更新播放线信息
watch(
  () => [currentFrameTs.value],
  () => {
    calculatePlayLineInfo()
  },
  { deep: true }
)

// 连接 webscokot
const handleConnectWS = async (shouldPlay = true) => {
  console.log('handleConnect shouldPlay:', shouldPlay)

  // 播放前初始化 currentFrameTs：如果有有效片段，默认为第一个有效片段的时间
  if (currentFrameTs.value === new Date().setHours(0, 0, 0, 0) && timestampList.value.length > 0) {
    currentFrameTs.value = timestampList.value[0].startTime
    console.log('【播放初始化】跳转到第一个有效片段时间:', currentFrameTs.value)
  }

  try {
    // if (!filePath.value) {
    //   message.warning('暂无回放文件')
    //   return
    // }
    // clientId devId
    const wsUrl =
      `${import.meta.env.VITE_VIDEO_PLAY_URL.replace(/^https/, 'wss')}` +
      `/oss/ws/play_by_time?dev_id=${clientId.value}&start_ts=${effectTimeRange.value.start_ts}&end_ts=${effectTimeRange.value.end_ts}&speed=${localSpeedRate.value}`
    // `/oss/ws/play?file_path=${encodeURIComponent(filePath.value)}&speed=${localSpeedRate.value}&ts=${currentFrameTs.value}`

    console.log('【WebSocket】回放 URL🔗', wsUrl)
    wsRef.value = new WebSocket(wsUrl)

    const ws = wsRef.value

    ws.onopen = () => {
      console.log('【WebSocket】回放 连接已打开✅')
      // 首次连接时，跳转到当前帧时间
      sendCmd({ cmd: 'seek', ts: currentFrameTs.value })
      // 根据传入的参数决定是否发送resume命令
      if (shouldPlay) {
        sendCmd({ cmd: 'resume' })
        playing.value = true
      } else {
        sendCmd({ cmd: 'pause' })
        playing.value = false
      }
    }

    // 处理播放结束消息
    const handleEndMessage = () => {
      ws.close()
    }

    // 处理帧时间更新
    const handleFrameUpdate = (ts: number) => {
      currentFrameTs.value = ts
      // 只有在播放状态下才更新进度条视图
      if (playing.value) {
        updateProgressBarView(ts)
      }
    }

    // 处理3D点云数据
    const handlePointCloudData = (pointClouds: CloudPoint[]) => {
      try {
        emit('pointCloud3dChange', pointClouds)
      } catch (e) {
        console.error('【WebSocket】回放 3D消息解析失败❌', e)
        emit('pointCloud3dChange', [])
      }
    }

    // 处理2D目标点数据
    const handleTargetData = (targetPoints: Array<[number, number, number, number]>) => {
      try {
        if (
          Array.isArray(targetPoints) &&
          targetPoints.length > 0 &&
          Array.isArray(targetPoints[0])
        ) {
          const currentIds = new Set<number>()
          targetPoints.forEach((item: number[]) => {
            if (item.length < 4) return
            const [x, y, z, id] = item
            currentIds.add(id)
            const pRadar = { x: 250, y: 250 }
            const point = convert_point_r2c({ x, y }, pRadar, Number(props.angle))

            if (!(id in targets)) {
              targets[id] = {
                x,
                y,
                z,
                id,
                displayX: point.x,
                displayY: point.y,
                lastX: x,
                lastY: y,
              }
            }

            const dx = x - targets[id].lastX
            const dy = y - targets[id].lastY
            if (Math.sqrt(dx * dx + dy * dy) > 2) {
              targets[id].x = x
              targets[id].y = y
              targets[id].z = z
              targets[id].lastX = x
              targets[id].lastY = y
              targets[id].displayX = point.x
              targets[id].displayY = point.y
            }
          })

          Object.keys(targets).forEach((key) => {
            const id = Number(key)
            if (!currentIds.has(id)) {
              delete targets[id]
            }
          })
        } else {
          Object.keys(targets).forEach((key) => delete targets[Number(key)])
        }
        // 更新2D目标点
        emit('targets2dChange', targets)

        // 更新3D目标点
        const radarTargets = targetPoints
        emit('radarTargetsChange', radarTargets)
      } catch (e) {
        console.error('【WebSocket】回放 2D消息解析失败❌', e)
      }
    }

    // 处理WebSocket消息
    ws.onmessage = (event) => {
      try {
        const messageEvet: WebMessageType = JSON.parse(event.data)
        console.log('【WebSocket】回放 收到消息📥️', messageEvet)

        // 处理错误消息
        if (messageEvet.error) {
          message.error(messageEvet.error)
          return
        }

        // 检查消息有效性
        // if (!messageEvet.ts && !messageEvet.msg) {
        //   return
        // }

        // 按类型处理消息
        // 1. 处理播放结束消息
        if (messageEvet.msg === 'end') {
          message.warning('当前时间无播放内容，已重置', 5)
          handleEndMessage()
          return
        }

        // 2. 更新当前帧时间戳
        if (messageEvet.ts) {
          handleFrameUpdate(messageEvet.ts)
        }

        // 更新当前帧的z_info
        if (messageEvet.z_info) {
          currentZInfo.value = messageEvet.z_info
        }

        // 3. 处理数据负载
        if (messageEvet.payload) {
          // 处理3D点云数据
          if (messageEvet.payload.cloud_points) {
            handlePointCloudData(messageEvet.payload.cloud_points)
          } else {
            emit('pointCloud3dChange', [])
          }

          // 处理2D目标点数据
          if (messageEvet.payload.tracker_targets) {
            handleTargetData(messageEvet.payload.tracker_targets)
          } else {
            emit('targets2dChange', {})
            emit('radarTargetsChange', [])
          }
        }
      } catch (e) {
        console.error('【WebSocket】回放 消息解析失败❌', e)
      }
    }

    ws.onerror = (error) => {
      console.error('【WebSocket】回放错误❌', error)
    }

    ws.onclose = () => {
      console.log('【WebSocket】回放 连接已关闭🚫')
      // 重置播放状态
      playing.value = false
      // 重置当前帧 ts
      currentFrameTs.value = new Date().setHours(0, 0, 0, 0)
      // 重置进度条视图
      updateProgressBarView(0)
      // 重置目标点
      Object.keys(targets).forEach((key) => delete targets[Number(key)])
      emit('targets2dChange', targets)
      emit('pointCloud3dChange', [])
      // message.warning('播放已结束，请重新播放')
    }
  } catch (err) {
    console.error('【WebSocket】回放 请求录像失败❌', err)
    throw err
  }
}

// 播放控制
const handlePlay = async () => {
  console.log('handlePlay')

  if (wsRef.value && wsRef.value.readyState === WebSocket.OPEN) {
    console.log('【WebSocket】回放连接中，执行播放')
    sendCmd({ cmd: 'resume' })
    playing.value = true
  } else {
    console.log('【WebSocket】回放未连接，连接并播放')
    playLoading.value = true
    try {
      await handleConnectWS()
    } finally {
      playLoading.value = false
    }
  }
}

const handlePause = () => {
  console.log('handlePause')
  sendCmd({ cmd: 'pause' })
  playing.value = false
}

// 倍速控制
const handleSpeedChange = () => {
  console.log('handleSpeedChange', localSpeedRate.value)
  if (!wsRef.value) return
  sendCmd({ cmd: 'set_speed', speed: localSpeedRate.value })
  sendCmd({ cmd: 'pause' })
  playing.value = false
}

// 时间选择器控制
const handleTimeChange = () => {
  console.log('handleTimeChange', currentFrameTs.value)
  if (!wsRef.value) return
  // 发送跳转命令到指定时间
  sendCmd({ cmd: 'seek', ts: Math.round(currentFrameTs.value) })
  sendCmd({ cmd: 'pause' })
  playing.value = false
}

/**
 * 发送播放指令到WebSocket
 * @param cmd resume播放 | pause暂停 | set_speed倍速 | seek跳转 | loop循环播放
 * @example
 * 播放，{ cmd: 'resume' }
 * 暂停，{ cmd: 'pause' }
 * 倍速，{ cmd: 'set_speed', speed: '1.0' }
 * 跳转，{ cmd: 'seek', ts: '1694500000000' }
 * 循环播放，{ cmd: 'loop', loop: true }
 */

// 命令队列管理
const commandQueue: Array<Record<string, string | number>> = []
let isProcessingQueue = false
const MAX_RETRY_COUNT = 3

// 处理命令队列
const processCommandQueue = () => {
  if (isProcessingQueue || commandQueue.length === 0) return

  isProcessingQueue = true
  const cmd = commandQueue.shift()

  if (cmd) {
    sendCommandWithRetry(cmd, 0)
  }
}

// 带重试机制的命令发送
const sendCommandWithRetry = (cmd: Record<string, string | number>, retryCount: number) => {
  const ws = wsRef.value
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    console.log('【WebSocket】回放已结束，不能操作⭕️')
    if (playing.value) {
      playing.value = false
    }
    isProcessingQueue = false
    processCommandQueue()
    return
  }

  try {
    ws.send(JSON.stringify(cmd))
    console.log('🟢 已发送 回放指令', cmd)

    // 播放状态下的seek命令，立即发送resume命令
    if (cmd['cmd'] === 'seek' && playing.value) {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ cmd: 'resume' }))
        console.log('🟣 已发送 继续播放指令')
      }
    }

    // 命令发送成功，继续处理队列
    setTimeout(() => {
      isProcessingQueue = false
      processCommandQueue()
    }, 50) // 添加小延迟，确保命令按顺序处理
  } catch (e) {
    console.error('【WebSocket】回放 指令发送失败❌', e, `(重试次数: ${retryCount})`)

    // 发送失败时，更新播放状态
    if (cmd['cmd'] === 'pause') {
      playing.value = false
    }

    // 重试机制
    if (retryCount < MAX_RETRY_COUNT) {
      console.log(`【WebSocket】回放 指令发送失败，${retryCount + 1}秒后重试...`, cmd)
      setTimeout(
        () => {
          sendCommandWithRetry(cmd, retryCount + 1)
        },
        1000 * (retryCount + 1)
      ) // 指数退避重试
    } else {
      console.error('【WebSocket】回放 指令发送失败，已达到最大重试次数', cmd)
      isProcessingQueue = false
      processCommandQueue()
    }
  }
}

const sendCmd = (cmd: Record<string, string | number>) => {
  // 将命令添加到队列
  commandQueue.push(cmd)
  // 开始处理队列
  processCommandQueue()
}

// 更新进度条视图
const updateProgressBarView = (currentTime: number) => {
  // 保持整个24小时的视图范围不变
  // 只更新当前帧时间戳，用于显示播放时间
  currentFrameTs.value = currentTime
  // 重新计算播放线信息
  calculatePlayLineInfo()
  // 重新绘制，确保播放线位置正确
  draw()
}

onMounted(() => {
  resetWsRef()
})

onUnmounted(() => {
  resetWsRef()
})

// 下载列表表头
const columns = [
  {
    title: '序号',
    key: 'index',
    dataIndex: 'index',
    align: 'center',
    width: 50,
  },
  {
    title: '文件名称',
    key: 'fileName',
    dataIndex: 'fileName',
    align: 'center',
  },
  {
    title: '事件类型',
    key: 'eventName',
    dataIndex: 'eventName',
    align: 'center',
  },
  {
    title: '操作',
    key: 'operation',
    dataIndex: 'operation',
    align: 'center',
    width: 120,
  },
]

const listLoading = ref(false)
const downloadFileList = computed(() => {
  return [...fallEventDownloadList.value, ...personInOutEventDownloadList.value]
})
const fallEventDownloadList = ref<
  { fileName: string; downloadUrl: string; eventName: string; type: string }[]
>([])
const personInOutEventDownloadList = ref<
  { fileName: string; downloadUrl: string; eventName: string; type: string }[]
>([])

const fetchEventDownloadUrl = async () => {
  try {
    listLoading.value = true
    const res = await queryEventTimeNodeFileUrls({
      devId: devId.value,
      date: dateTime.value.format('YYYY-MM-DD'),
      downloadFallEvent: selectedTags.value.includes('fall'),
      downloadPersonInOutEvent: selectedTags.value.includes('inout'),
      personInOutEvent: eventAxisData.value.personInOutEvent,
    })
    const fallEventDownloadUrl = res.data.fallEventDownloadUrl || []
    fallEventDownloadList.value = fallEventDownloadUrl.map((item) => ({
      fileName: item.match(/[^/]+$/)?.[0] ?? '',
      downloadUrl: `${import.meta.env.VITE_APP_HOST}${item.replace('/data', '/download')}`,
      eventName: '跌倒事件',
      type: 'fallEvent',
    }))
    const personInOutEventDownloadUrl = res.data.personInOutEventDownloadUrl || []
    personInOutEventDownloadList.value = personInOutEventDownloadUrl.map((item) => ({
      fileName: item.match(/[^/]+$/)?.[0] ?? '',
      downloadUrl: `${import.meta.env.VITE_APP_HOST}${item.replace('/data', '/download')}`,
      eventName: '人员存在',
      type: 'personInOutEvent',
    }))
  } catch (e) {
    console.error('【WebSocket】回放 查询事件文件下载地址失败❌', e)
  } finally {
    listLoading.value = false
  }
}

const selectedRowKeys = ref<
  { fileName: string; downloadUrl: string; eventName: string; type: string }[]
>([])
const selectedRows = ref<string[]>([])
const hasSelected = computed(() => selectedRowKeys.value.length > 0)

const onSelectChange = (
  keyArray: { fileName: string; downloadUrl: string; eventName: string; type: string }[],
  selectRows: string[]
) => {
  console.log('onSelectChange', keyArray, selectRows)
  selectedRowKeys.value = keyArray
  selectedRows.value = selectRows
}

// 处理批量文件下载
const handleBatchDownload = async () => {
  console.log('handleBatchDownload', selectedRowKeys.value)
  const results = []
  for (const item of selectedRowKeys.value) {
    try {
      const result = await downloadHandler(item.downloadUrl, item.eventName)
      results.push({ status: 'fulfilled', value: result })
      // 添加延迟，确保浏览器有足够时间处理每个下载请求
      await new Promise((resolve) => setTimeout(resolve, 100))
    } catch (error) {
      results.push({ status: 'rejected', reason: error })
    }
  }
  message.success('批量下载完成')
}

// 处理单个文件下载
const handleDownloadOne = (downloadUrl: string, eventName: string) => {
  // console.log('handleDownloadOne', downloadUrl, eventName)
  downloadHandler(downloadUrl, eventName)
    .then(() => {
      message.success('下载成功')
    })
    .catch(() => {
      message.error('下载失败')
    })
}

/**
 * 下载文件
 * @description 用于下载文件
 * @param downloadUrl 下载地址
 * @example
 * downloadHandler('https://example.com/file.rrf.json?token=123456')
 */
const downloadHandler = (downloadUrl: string, eventName: string) => {
  return new Promise((resolve, reject) => {
    try {
      console.log('downloadHandler', downloadUrl, eventName)
      const link = document.createElement('a')
      link.href = downloadUrl

      const urlParts = downloadUrl.split('/')
      // const filename = urlParts[urlParts.length - 1].replace('.rrf.json', '').split('?')[0]
      const originalFilename = urlParts[urlParts.length - 1].replace('.rrf.json', '').split('?')[0]
      const filename = eventName ? `${eventName}_${originalFilename}` : originalFilename
      link.download = filename
      link.setAttribute('type', 'application/octet-stream')

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      resolve(`文件${filename}下载成功`)
    } catch (error) {
      console.log('下载失败❌', error)
      reject(error)
    }
  })
}
</script>

<style scoped lang="less">
.video-progress-bar {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 260px;

  .bar-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .type-wrapper-tipbar {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 8px;
    position: relative;

    .tag-tag {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
    }

    .custom-tag {
      display: inline-flex;
      align-items: center;
      color: #000;
      font-size: 12px;
      position: relative;
      overflow: hidden;
      min-width: auto;
      // padding: 4px 11px;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      user-select: none;

      &.tag-disabled {
        .tag-icon {
          background-color: #d9d9d9 !important;
        }

        .tag-text {
          color: #999;
        }

        .tag-count {
          color: #999;
        }
      }

      .tag-icon {
        display: inline-block;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        margin-right: 4px;
        flex-shrink: 0;
      }

      .tag-icon.tag-loading {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: #1890ff;
        animation: loadingCircle 1s infinite linear;
      }

      .tag-text {
        z-index: 1;
        color: #000;
      }

      .tag-count {
        font-size: 11px;
        color: #666;
        margin-left: 4px;
        flex-shrink: 0;
      }
    }

    // 加载文字样式（与 custom-tag 同级）
    .loading-text {
      color: #666;
      font-size: 12px;
      // padding: 4px 11px;
      align-self: center;
    }
  }

  .type-wrapper {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 8px;
    min-width: 350px;
    border-radius: 4px;
    height: 100%;

    .tag-tag {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 5px;
    }

    .custom-tag {
      display: inline-flex;
      align-items: center;
      padding: 4px 11px;
      border-radius: 10px;
      color: #ddd;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      min-width: auto;
      user-select: none;

      &:hover {
        opacity: 0.8;
      }

      .tag-text {
        z-index: 1;
      }

      /* 选中效果：背景蒙版 + 对号 */
      .tag-selected-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;

        .tag-selected-mask {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 250, 0, 0.8);
          border-radius: inherit;
          animation: fadeIn 0.2s ease;
        }

        .tag-check {
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          z-index: 3;
          animation: scaleIn 0.2s ease;
        }
      }
    }
  }

  .time-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .skeleton-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background-color: #fff;
    z-index: 100;
  }

  // 主容器
  .main-container {
    position: relative;
    width: 100%;
    height: 90px;
    background-color: #f5f5dc;
    border-radius: 4px;
    overflow: visible;
    cursor: default;

    // Canvas 元素
    canvas {
      display: block;
      width: 100%;
      height: 100%;
      cursor: default;
    }

    // 悬停竖线
    .hover-line {
      position: absolute;
      left: 0;
      top: 0;
      height: calc(100% - 30px);
      width: 2px;
      background-color: #1677ff; // 蓝色
      // background-color: rgba(82, 196, 26, 0.9); // 淡绿色，半透明
      pointer-events: none;
      z-index: 15;
    }

    // 悬停信息提示
    .hover-info {
      position: absolute;
      bottom: 0;
      transform: translateX(-50%);
      background-color: rgba(22, 119, 255, 0.9); // 蓝色，半透明
      // background-color: rgba(82, 196, 26, 0.9); // 淡绿色，半透明
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      pointer-events: none;
      z-index: 21;
      white-space: nowrap;
    }

    // 播放线信息展示
    .play-line-info {
      position: absolute;
      bottom: 0;
      transform: translateX(-50%);
      background-color: rgba(22, 119, 255, 0.9); // 蓝色，半透明
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      pointer-events: none;
      z-index: 20;
      white-space: nowrap;

      // 播放状态时的发光效果（只应用于边框）
      &.play-line-glow {
        // 骨架屏流动效果
        background: linear-gradient(
          90deg,
          rgba(22, 119, 255, 0.9) 0%,
          rgba(22, 119, 255, 0.9) 25%,
          rgba(100, 181, 255, 0.95) 50%,
          rgba(22, 119, 255, 0.9) 75%,
          rgba(22, 119, 255, 0.9) 100%
        );
        background-size: 200% 100%;
        animation: skeleton-loading 2s ease-in-out infinite;
        box-shadow:
          0 0 4px rgba(22, 119, 255, 0.6),
          0 0 8px rgba(22, 119, 255, 0.4);
      }
    }

    // 骨架屏流动动画
    @keyframes skeleton-loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  }

  // 控制按钮区域 - 左右 flex 布局
  .control-buttons {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    height: 150px;
  }

  // 时间戳列表区域
  .timestampList {
    display: flex;
    align-content: flex-start;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px;
    height: 100%;
    overflow-y: auto;
    background-color: #f5f5f5;
    border-radius: 4px;
    flex-grow: 1;
    position: relative;

    // 滚动条样式
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background-color: #f5f5f5;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #dbdada;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: #f0f0f0;
    }

    // 时间戳 tag
    .timestamp-tag {
      display: inline-flex;
      align-items: center;
      padding: 4px 11px;
      border-radius: 10px;
      color: #333;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      user-select: none;
      border: 1px solid transparent;
      height: 24px;
      width: 150px;

      &:hover {
        opacity: 0.8;
        transform: translateY(-2px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      .tag-icon {
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        margin-right: 6px;
        flex-shrink: 0;
      }

      .tag-text {
        color: #333;
        font-weight: 500;
        white-space: nowrap;
      }
    }
  }
}

@keyframes loadingCircle {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.fast-btn-operation {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
